import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import BaseTool from '@glass-designer/tools/baseTool'
import MathsUtils from '@/utils/mathsUtils'

import GlassShape from '@glass-designer/model/glassShape'
import LinearRulerObject from '@glass-designer/objects/linearRulerObject'

import GlassDesignerTypes from '@glass-designer/types'
import GlassBaseHole from '../../model/glassBaseHole'


// Center Cutout Move Tool
// Tool used when user clicked & dragged on a move button for a center cutout
export default class CenterCutoutMoveTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.centerCutoutMoveTool
    this.moveDirection = new THREE.Vector2(0, 0)
    this.moveStartPosition = new THREE.Vector2(0, 0)

    this.holeReferencePoint = new THREE.Vector2(0, 0)
    this.selectedShape = null
    this.selectedHoleList = []

    this.linearRulerObj = new LinearRulerObject(this.editor, {
      colour: '#FF0000',
      displayAsChange: true
    })
    this.linearRulerObj.setVisible(false)
    this.linearRulerObj.setObjectEventListener(this)
    this.addChildObject(this.linearRulerObj)
  }


  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive()

    this.selectedShape = null
    this.selectedHoleList = []

    let selectedList = this.editor.getSelected()
    selectedList.forEach(selectedObj => {
      if (selectedObj instanceof GlassBaseHole) {
        
        if(!this.selectedHoleList.find(hole => hole.id == selectedObj.id)) {
          this.selectedHoleList.push(selectedObj)
          this.selectedShape = selectedObj.parentObject
        }
      }
    })

    if(this.selectedHoleList.length == 0)
      return

    this.holeReferencePoint.copy(this.selectedHoleList[0].worldPoint)

    let moveHandle = this.editor.getMoveHandleObject()
    let handlePos = this.holeReferencePoint.clone()
    let rulerMeasurePos = handlePos

    if(options.direction) {
      this.moveDirection.copy(options.direction)

      // Restrict the move handle axis to the axis we're moving
      moveHandle.setupHandles({
        horizontalEnabled: Math.abs(this.moveDirection.x) > 0.5,
        verticalEnabled: Math.abs(this.moveDirection.y) > 0.5
      })
    }
    this.moveStartPosition.copy(handlePos)

    moveHandle.setPosition(handlePos)
    moveHandle.setVisible(true)

    // Display an indicator on the start/end of the segment showing the change difference
    this.linearRulerObj.setStartPosition(rulerMeasurePos)
    this.linearRulerObj.setEndPosition(rulerMeasurePos)
    this.linearRulerObj.setOutDirection(new THREE.Vector2(1, 0))
    this.linearRulerObj.setVisible(true)
    this.linearRulerObj.updateSceneObject()

    this.updateRulerPosition(rulerMeasurePos)

    // Fill in options
    var popoverOptions = {
      value: 0,
      displayUserFixedField: false,
      displaySegmentMoveOptions: false,
      isUserFixed: false,
      allowPlusMinus: true,
      title: 'Move',
      addon: 'mm',
      context: this,
      updateCallback: this.updateCutoutMove,
      cancelCallback: this.cancelCutoutMove,
      applyCallback: this.applyCutoutMove
    }

    var displayOptions = {
      horizontalPosition: this._calculatePopoverHorizontalPosition()
    }

    this.editor.component.displayNumberEntryPopover(popoverOptions, options.position, displayOptions)
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.linearRulerObj.setVisible(false)

    this.editor.component.hidePopover()
  }


  updateCutoutMove(context, options) {
    context.moveHole(false, options.value)
  }

  applyCutoutMove(context, options) {
    context.moveHole(true, options.value)    
  }

  cancelCutoutMove(context) {
    context.editor.cancelActiveTool()
  }

  moveHole(applyHoleMove, distance) {
    // TODO: Figure out update mode (display highlight version of holes?)
    
    if(applyHoleMove) {
      try {
        this.editor.startUndoGroup('Updating Shape Segment')
        this.editor.addUndoShape(this.selectedShape)

        this.selectedHoleList.forEach(hole => {
          let offset = hole.worldPoint.clone().sub(this.holeReferencePoint)

          let newPosition = this.moveStartPosition.clone().addScaledVector(this.moveDirection, distance).add(offset)

          this.selectedShape.moveCenterCutout(hole, newPosition)
        })

        this.selectedShape.internalItemsUpdated()
        this.editor.onShapeUpdated(this.selectedShape)

        this.editor.closeCurrentUndoGroup()
      } catch(err) {
        this.editor.cancelCurrentUndoGroup()
        throw err
      }
    }
  }

  onReceiveObjectEvent(object, eventType, data) {
    let moveHandle = this.editor.getMoveHandleObject()
    
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      if(object == moveHandle) {
        this.updateRulerPosition(moveHandle.position)

        // Calculate distance
        let distance = Math.round(MathsUtils.calculateDistanceToPlane2D(this.moveDirection, this.moveStartPosition, moveHandle.position))

        // Update popover
        this.editor.component.updateNumberEntryPopover(distance)

        // Move hole
        this.moveHole(false, distance)
      }
    }
  }


  updateRulerPosition(position) {
    var outDir = position.clone().sub(this.linearRulerObj.startPosition)
    outDir.normalize()
    outDir.set(outDir.y, -outDir.x)

    // Set to always face away from the shape
    if(MathsUtils.calculateDistanceToPlane2D(outDir, position, this.holeReferencePoint) > 0)
      outDir.negate()

    this.linearRulerObj.setEndPosition(position)
    this.linearRulerObj.setOutDirection(outDir)
    this.linearRulerObj.updateSceneObject()

  }
}
